import { BookOpen, Loader2, Package } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Container } from '@/components/layout/Container'
import { CardSlider } from '@/components/ui/CardSlider'
import { AnimateIn } from '@/components/motion/AnimateIn'
import { EbookEditionCard } from '@/components/magazine/EbookEditionCard'
import { HomeProductCard } from '@/components/premium/HomeProductCard'
import { HomeSplitSliderPanel } from '@/components/home/HomeSplitSliderPanel'
import { PATHS } from '@/routes/paths'
import { useMagazines } from '@/hooks/api/useMagazines'
import { usePremiumPackages } from '@/hooks/api/usePremiumPackages'

/** Shared min height so both split columns align on desktop */
const SPLIT_SLIDER_MIN_H = 'min-h-[328px] sm:min-h-[348px] md:min-h-[368px]'

/** Equal padding both columns — less top, more bottom */
const SPLIT_PANEL_PAD =
  'px-5 pt-3 pb-10 sm:px-6 sm:pt-3.5 sm:pb-12 md:px-7 md:pb-14'

export function MagazineSection() {
  const { issues } = useMagazines()
  const { otherProducts, isLoading: productsLoading } = usePremiumPackages()

  const featuredIssues = issues.slice(0, 10)
  const featuredProducts = otherProducts.slice(0, 10)

  if (featuredIssues.length === 0 && featuredProducts.length === 0 && !productsLoading) {
    return null
  }

  const showMagazines = featuredIssues.length > 0
  const showProducts = featuredProducts.length > 0 || productsLoading

  return (
    <section className="bg-linear-to-b from-neutral-50 via-white to-neutral-50 py-10 md:py-14">
      <Container>
        <AnimateIn>
          <div className="overflow-hidden rounded-2xl border border-neutral-200/90 bg-white shadow-[0_8px_30px_rgba(15,93,154,0.06)] lg:grid lg:grid-cols-2 lg:items-stretch">
            {showMagazines && (
              <div
                className={cn(
                  'flex h-full flex-col border-b border-neutral-100 lg:border-b-0 lg:border-r',
                  SPLIT_PANEL_PAD,
                )}
              >
                <HomeSplitSliderPanel
                  className="h-full flex-1"
                  title="QBC E-Books"
                  subtitle="Monthly property editions — read the latest cover stories."
                  href={PATHS.magazine}
                  linkLabel="All Editions"
                  icon={BookOpen}
                  accent="primary"
                  sliderMinHeight={SPLIT_SLIDER_MIN_H}
                >
                  <CardSlider
                    size="ebook-half"
                    navVariant="header"
                    hideScrollbar
                    flushStart
                    equalHeight
                    className="h-full"
                  >
                    {featuredIssues.map((issue) => (
                      <EbookEditionCard key={issue.id} issue={issue} className="h-full" compact split />
                    ))}
                  </CardSlider>
                </HomeSplitSliderPanel>
              </div>
            )}

            {showProducts && (
              <div className={cn('flex h-full flex-col', SPLIT_PANEL_PAD)}>
                <HomeSplitSliderPanel
                  className="h-full flex-1"
                  title="Our Products"
                  subtitle="Guide books, magazine packages & digital courses."
                  href={PATHS.premiumAllProducts}
                  linkLabel="All Products"
                  icon={Package}
                  accent="primary"
                  sliderMinHeight={SPLIT_SLIDER_MIN_H}
                >
                  {productsLoading ? (
                    <div className={`flex items-center justify-center ${SPLIT_SLIDER_MIN_H}`}>
                      <Loader2 className="h-7 w-7 animate-spin text-primary-500" />
                    </div>
                  ) : (
                    <CardSlider
                      size="product-half"
                      navVariant="header"
                      hideScrollbar
                      flushStart
                      equalHeight
                      className="h-full"
                    >
                      {featuredProducts.map((pkg) => (
                        <HomeProductCard key={pkg.id} pkg={pkg} variant="compact" className="h-full" split focusGallery />
                      ))}
                    </CardSlider>
                  )}
                </HomeSplitSliderPanel>
              </div>
            )}
          </div>

          {!showMagazines && showProducts && (
            <p className="sr-only">Magazine editions unavailable</p>
          )}
        </AnimateIn>
      </Container>
    </section>
  )
}
